import type { CSSProperties } from "react";
import { getUIColorPalette } from "../../../theme/ColorPalette";

/**
 * Resolves container styles for the compact itinerary debug toolbar.
 *
 * @param isDarkUi Whether dark palette tokens should be used.
 * @returns Inline styles for the debug toolbar shell.
 */
export const getItineraryDebugToolbarContainerStyle = ( isDarkUi: boolean ): CSSProperties => {
    const palette = getUIColorPalette( isDarkUi );

    return {
        "display":      "flex",
        "flexWrap":     "wrap",
        "alignItems":   "center",
        "gap":          "0.25rem",
        "margin":       "0.125rem 0.375rem 0.125rem",
        "padding":      "0.125rem 0.25rem",
        "borderRadius": "0.375rem",
        "border":       `1px dashed ${ isDarkUi ? "#f59e0b" : "#d97706" }`,
        "background":   isDarkUi ? "rgba(245, 158, 11, 0.1)" : "rgba(251, 191, 36, 0.14)",
        "color":        palette.overlayTextPrimary,
    };
};

/**
 * Resolves inline label styles for the debug toolbar prefix.
 *
 * @param isDarkUi Whether dark palette tokens should be used.
 * @returns Inline styles for the compact toolbar label.
 */
export const getItineraryDebugToolbarLabelStyle = ( isDarkUi: boolean ): CSSProperties => {
    return {
        "flexShrink":    0,
        "fontSize":      "0.6rem",
        "fontWeight":    700,
        "letterSpacing": "0.03em",
        "lineHeight":    1,
        "textTransform": "uppercase",
        "color":         isDarkUi ? "#fbbf24" : "#b45309",
        "marginRight":   "0.125rem",
    };
};

/**
 * Resolves pill button styles shared by debug relaunch actions.
 *
 * @param isDarkUi Whether dark palette tokens should be used.
 * @param isDisabled Whether the action is currently unavailable.
 * @returns Inline styles for a debug action pill.
 */
export const getItineraryDebugActionButtonStyle = (
    isDarkUi: boolean,
    isDisabled: boolean,
): CSSProperties => {
    const palette = getUIColorPalette( isDarkUi );

    return {
        "padding":       "2px 4px 1px 4px",
        "fontSize":      "0.625rem",
        "lineHeight":    1.1,
        "borderWidth":   1,
        "borderStyle":   "solid",
        "borderColor":   palette.overlayBorderStrong,
        "borderRadius":  "0.25rem",
        "color":         palette.overlayTextPrimary,
        "opacity":       isDisabled ? 0.55 : 1,
        "cursor":        isDisabled ? "not-allowed" : "pointer",
        "pointerEvents": isDisabled ? "none" : "auto",
        "whiteSpace":    "nowrap",
    };
};
